// Real-app entry — the one impure mount for `index.html`. Hands the pure
// `AppShellHost` the concrete `httpPorts` (fetch against the running server)
// through the `AppPorts` seam. (The former backendless `main.tsx`/`app.html`
// preview on `mockPorts` was retired — the desktop shell dogfoods this same
// path against an in-process server.)

import { I18nProvider } from "@lingui/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { activateLocale, detectLocale, i18n } from "../i18n/setup.js";
import { AppShellHost } from "./AppShellHost.js";
import { AiUsageHost } from "./aiUsage/AiUsageHost.js";
import { isAiUsagePath } from "./aiUsage/route.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { httpPorts } from "./httpPorts.js";
import { AppPortsProvider } from "./ports.js";
import { ThemeProvider } from "./theme.js";

// The dev-only event-log fixture switcher (DevLogPicker) is no longer mounted
// here: it now lives INSIDE Settings → Advanced (see AppShellHost), reachable
// but never floating on the main canvas.

const container = document.getElementById("root");
if (container === null) {
    throw new Error("app root element missing");
}

// Impure: pick and activate the locale before the tree renders.
activateLocale(detectLocale());

// The operator's AI-usage pages are their own small tree (admin frame, no
// app shell); every other path is the app.
const page = isAiUsagePath(window.location.pathname) ? (
    <AiUsageHost />
) : (
    <AppPortsProvider value={httpPorts}>
        <AppShellHost />
    </AppPortsProvider>
);

createRoot(container).render(
    <StrictMode>
        <I18nProvider i18n={i18n}>
            <ErrorBoundary>
                <ThemeProvider>{page}</ThemeProvider>
            </ErrorBoundary>
        </I18nProvider>
    </StrictMode>,
);
